const MOBILE_CONSTRUCTION_DOCK_SELECTOR = '.mobile-construction-dock';
const INTERACTIVE_CONTROL_SELECTOR = [
  'a',
  'button',
  'input',
  'label',
  'select',
  'summary',
  'textarea',
  '[contenteditable="true"]',
  '[role="button"]',
  '[role="link"]',
  '[role="tab"]',
].join(',');

const MAX_DOUBLE_TAP_DELAY_MS = 350;
const MAX_DOUBLE_TAP_DISTANCE_PX = 32;
const MOVE_TOLERANCE_PX = 10;

interface TapPoint {
  x: number;
  y: number;
  time: number;
}

function elementTarget(target: EventTarget | null): Element | null {
  if (target instanceof Element) return target;
  return target instanceof Node ? target.parentElement : null;
}

function distanceSquared(a: Pick<TapPoint, 'x' | 'y'>, b: Pick<TapPoint, 'x' | 'y'>) {
  const deltaX = a.x - b.x;
  const deltaY = a.y - b.y;
  return deltaX * deltaX + deltaY * deltaY;
}

function isBlankConstructionSpace(target: EventTarget | null) {
  const element = elementTarget(target);
  return Boolean(
    element?.closest(MOBILE_CONSTRUCTION_DOCK_SELECTOR) &&
    !element.closest(INTERACTIVE_CONTROL_SELECTOR),
  );
}

export function installMobileBlankDoubleTapGuard(doc: Document = document) {
  let touchStart: Pick<TapPoint, 'x' | 'y'> | null = null;
  let touchMoved = false;
  let previousBlankTap: TapPoint | null = null;

  const resetActiveTouch = () => {
    touchStart = null;
    touchMoved = false;
  };

  const handleTouchStart = (event: TouchEvent) => {
    if (event.touches.length !== 1) {
      resetActiveTouch();
      previousBlankTap = null;
      return;
    }

    const touch = event.touches[0];
    touchStart = { x: touch.clientX, y: touch.clientY };
    touchMoved = false;
  };

  const handleTouchMove = (event: TouchEvent) => {
    if (!touchStart || event.touches.length !== 1) return;
    const touch = event.touches[0];
    if (
      distanceSquared(touchStart, { x: touch.clientX, y: touch.clientY }) >
      MOVE_TOLERANCE_PX * MOVE_TOLERANCE_PX
    ) {
      touchMoved = true;
    }
  };

  const handleTouchEnd = (event: TouchEvent) => {
    const touch = event.changedTouches[0];
    const isBlankTap =
      Boolean(touchStart) &&
      !touchMoved &&
      event.changedTouches.length === 1 &&
      isBlankConstructionSpace(event.target);

    resetActiveTouch();

    if (!isBlankTap || !touch) {
      previousBlankTap = null;
      return;
    }

    const tap: TapPoint = {
      x: touch.clientX,
      y: touch.clientY,
      time: event.timeStamp,
    };
    const isRapidSecondTap =
      previousBlankTap !== null &&
      tap.time - previousBlankTap.time <= MAX_DOUBLE_TAP_DELAY_MS &&
      distanceSquared(tap, previousBlankTap) <=
        MAX_DOUBLE_TAP_DISTANCE_PX * MAX_DOUBLE_TAP_DISTANCE_PX;

    if (isRapidSecondTap) {
      event.preventDefault();
      previousBlankTap = null;
      return;
    }

    previousBlankTap = tap;
  };

  const handleTouchCancel = () => {
    resetActiveTouch();
    previousBlankTap = null;
  };

  const handleDoubleClick = (event: MouseEvent) => {
    if (isBlankConstructionSpace(event.target)) event.preventDefault();
  };

  doc.addEventListener('touchstart', handleTouchStart, { passive: true });
  doc.addEventListener('touchmove', handleTouchMove, { passive: true });
  doc.addEventListener('touchend', handleTouchEnd, { passive: false });
  doc.addEventListener('touchcancel', handleTouchCancel, { passive: true });
  doc.addEventListener('dblclick', handleDoubleClick);

  return () => {
    doc.removeEventListener('touchstart', handleTouchStart);
    doc.removeEventListener('touchmove', handleTouchMove);
    doc.removeEventListener('touchend', handleTouchEnd);
    doc.removeEventListener('touchcancel', handleTouchCancel);
    doc.removeEventListener('dblclick', handleDoubleClick);
  };
}
